import React, { useState } from 'react';
import { Flex, Typography, Button } from 'antd';
import { ClockCircleOutlined, CalendarOutlined, CloseOutlined, ExportOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
  formatSubscriptionEndDate,
  buildSubscriptionExpiringSoonBannerMessage,
  getSelfOnboardingPortalUrl,
  shouldShowSelfOnboardingPortalLink,
} from '../utils/subscriptionPlan';
import {
  openManageProfileLink,
  usesManageProfileLinkForSelfOnboarding,
} from '../utils/openManageProfileLink';
import SubscriptionCountdown from './SubscriptionCountdown';
import SubscriptionRemainingTag from './SubscriptionRemainingTag';

const buildBannerStyle = (isDarkMode) => ({
  marginBottom: 20,
  borderRadius: 14,
  overflow: 'hidden',
  border: `1px solid ${isDarkMode ? 'rgba(22, 119, 255, 0.35)' : 'rgba(22, 119, 255, 0.2)'}`,
  background: isDarkMode
    ? 'linear-gradient(135deg, rgba(22, 119, 255, 0.14) 0%, rgba(24, 28, 36, 0.98) 50%, #1f1f1f 100%)'
    : 'linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 45%, #ffffff 100%)',
  boxShadow: isDarkMode
    ? '0 4px 24px rgba(0, 0, 0, 0.35)'
    : '0 4px 20px rgba(22, 119, 255, 0.1)',
  position: 'relative',
});

const iconWrapStyle = {
  width: 48,
  height: 48,
  borderRadius: 12,
  background: 'linear-gradient(135deg, #1677ff 0%, #69c0ff 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 22,
  flexShrink: 0,
  boxShadow: '0 4px 12px rgba(22, 119, 255, 0.35)',
};

export default function SubscriptionExpiringSoonBanner({
  endDate,
  isDarkMode = false,
  onClose,
  roleName,
  subscriptionPlanDetails = null,
}) {
  const authToken = useSelector((state) => state.auth.token);
  const [portalLoading, setPortalLoading] = useState(false);
  const formattedEnd = formatSubscriptionEndDate(endDate);
  const portalUrl = getSelfOnboardingPortalUrl();
  const useManageProfileLink = usesManageProfileLinkForSelfOnboarding(roleName, authToken);
  const showPortalLink = shouldShowSelfOnboardingPortalLink(
    roleName,
    subscriptionPlanDetails,
    authToken
  );
  const message = buildSubscriptionExpiringSoonBannerMessage(endDate, roleName, authToken);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      await openManageProfileLink(authToken);
    } finally {
      setPortalLoading(false);
    }
  };

  const canShowPortalButton =
    showPortalLink && (useManageProfileLink || portalUrl);

  return (
    <div role="alert" style={buildBannerStyle(isDarkMode)}>
      <Button
        type="text"
        aria-label="Close reminder"
        icon={<CloseOutlined />}
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.45)',
        }}
      />
      <Flex align="center" gap={16} wrap="wrap" style={{ padding: '16px 44px 16px 20px' }}>
        <div style={iconWrapStyle}>
          <ClockCircleOutlined />
        </div>

        <Flex vertical style={{ flex: 1, minWidth: 220 }} gap={6}>
          <Flex align="center" gap={8} wrap="wrap">
            <Typography.Text
              strong
              style={{
                fontSize: 16,
                letterSpacing: '-0.02em',
                color: isDarkMode ? '#e6f4ff' : '#003a8c',
              }}
            >
              Subscription ending soon
            </Typography.Text>
            <SubscriptionRemainingTag endDate={endDate} />
          </Flex>
          <Typography.Text
            style={{
              fontSize: 13,
              lineHeight: 1.55,
              color: isDarkMode ? 'rgba(230, 244, 255, 0.75)' : 'rgba(0, 0, 0, 0.65)',
            }}
          >
            {message}
          </Typography.Text>
          <SubscriptionCountdown endDate={endDate} label="Countdown to expiry" />
          {canShowPortalButton ? (
            <Button
              type="link"
              size="small"
              icon={<ExportOutlined />}
              href={useManageProfileLink ? undefined : portalUrl}
              target={useManageProfileLink ? undefined : '_blank'}
              rel={useManageProfileLink ? undefined : 'noopener noreferrer'}
              onClick={useManageProfileLink ? handleOpenPortal : undefined}
              loading={portalLoading}
              style={{ padding: 0, height: 'auto', alignSelf: 'flex-start' }}
            >
              Open Manage Subscription
            </Button>
          ) : null}
        </Flex>

        {formattedEnd && (
          <Flex
            align="center"
            gap={8}
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              background: isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${isDarkMode ? 'rgba(22, 119, 255, 0.25)' : 'rgba(22, 119, 255, 0.12)'}`,
            }}
          >
            <CalendarOutlined style={{ color: '#1677ff', fontSize: 16 }} />
            <Flex vertical gap={0}>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                Expiry date & time
              </Typography.Text>
              <Typography.Text strong style={{ fontSize: 14, color: isDarkMode ? '#91caff' : '#0958d9' }}>
                {formattedEnd}
              </Typography.Text>
            </Flex>
          </Flex>
        )}
      </Flex>
    </div>
  );
}
